<template>
    <div class="ui-page">
        <ui-page-header title="Sedes e aeroportos"
            subtitle="De onde as equipes saem e os aeroportos que entram na comparação entre carro e avião. A posição do aeroporto é a do município (basta para o trecho de carro).">
            <v-btn outlined to="/prospeccao/rota"><v-icon left small>mdi-arrow-left</v-icon>Roteiros</v-btn>
        </ui-page-header>

        <ui-section title="Sedes" class="mb-4" description="A sede padrão abre as sugestões e os roteiros novos. Cada roteiro pode sair de outra sede.">
            <template #actions>
                <v-btn small color="primary" depressed @click="openOffice()"><v-icon left small>mdi-plus</v-icon>Sede</v-btn>
            </template>
            <table class="po-table">
                <thead><tr><th>Sede</th><th>Cidade</th><th>Aeroporto de saída</th><th></th><th></th></tr></thead>
                <tbody>
                    <tr v-for="office in offices" :key="office.id">
                        <td><strong>{{ office.name }}</strong></td>
                        <td>{{ office.municipality ? `${office.municipality.name}/${office.municipality.uf}` : "—" }}</td>
                        <td>{{ office.airport ? `${office.airport.iata} · ${office.airport.city}/${office.airport.uf}` : "sem aeroporto (só carro)" }}</td>
                        <td><ui-badge v-if="office.is_default" tone="primary" text="Padrão" /></td>
                        <td class="r">
                            <ui-row-actions :actions="[
                                { text: 'Editar', icon: 'mdi-pencil-outline', handler: () => openOffice(office) , disabled: !$allowed('edit') },
                                { text: 'Tornar padrão', icon: 'mdi-star-outline', hidden: office.is_default, handler: () => makeDefault(office) },
                                { text: 'Excluir', icon: 'mdi-delete-outline', danger: true, disabled: !$allowed('delete') || offices.length === 1, handler: () => removeOffice(office) },
                            ]" />
                        </td>
                    </tr>
                </tbody>
            </table>
            <div class="ui-divider"></div>
            <div class="po-threshold">
                <v-text-field v-model.number="threshold" type="number" min="1" max="24" step="0.5" label="Comparar com avião acima de (horas de carro)" dense outlined hide-details style="max-width: 300px;"></v-text-field>
                <v-btn v-if="$allowed('prospect.settings')" small outlined :loading="savingThreshold" @click="saveThreshold">Salvar</v-btn>
                <span class="ui-small ui-muted">Abaixo disso a sugestão é sempre carro. Acima, avião entra quando chega antes.</span>
            </div>
        </ui-section>

        <ui-table-card>
            <template #toolbar>
                <ui-pills v-model="airportScope" :items="airportScopeItems" />
                <v-text-field v-model="airportSearch" dense outlined hide-details clearable prepend-inner-icon="mdi-magnify" label="Código, cidade ou UF" style="max-width: 240px;"></v-text-field>
                <v-btn small outlined class="ml-auto" @click="openAirport"><v-icon left small>mdi-plus</v-icon>Aeroporto</v-btn>
            </template>
            <v-data-table :headers="airportHeaders" :items="filteredAirports" :loading="loading" hide-default-footer :items-per-page="-1" disable-sort
                no-data-text="Nenhum aeroporto neste filtro" loading-text="Carregando...">
                <template v-slot:[`item.iata`]="{ item }"><strong>{{ item.iata }}</strong></template>
                <template v-slot:[`item.name`]="{ item }">
                    <div class="ui-cell-main">{{ item.name }}</div>
                    <div class="ui-cell-sub">{{ item.city }}/{{ item.uf }}</div>
                </template>
                <template v-slot:[`item.level`]="{ item }">
                    <v-select :value="item.level" :items="levelItems" dense outlined hide-details :menu-props="{ offsetY: true }" style="max-width: 190px;" @change="(level) => updateAirport(item, { level })"></v-select>
                </template>
                <template v-slot:[`item.active`]="{ item }">
                    <v-switch :input-value="item.active" dense hide-details inset class="mt-0" :aria-label="`Usar ${item.iata}`" @change="(active) => updateAirport(item, { active })"></v-switch>
                </template>
            </v-data-table>
        </ui-table-card>
        <p class="ui-small ui-muted mt-2">Porte: “grande” recebe voo direto de qualquer lugar; entre duas capitais o voo também é direto; nos demais casos a estimativa conta uma escala. Desative aeroportos sem voo regular.</p>

        <v-dialog v-model="office.open" max-width="520" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">{{ office.id ? "Editar sede" : "Nova sede" }}</h3>
                        <p class="ui-section-description">Sem aeroporto escolhido, a plataforma usa o mais perto da cidade.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <v-text-field v-model="office.name" label="Nome" outlined dense hide-details="auto" class="mb-3"></v-text-field>
                    <v-autocomplete v-model="office.municipality_id" :items="office.cityOptions" item-value="id" :item-text="cityText" :search-input.sync="office.search"
                        label="Cidade" outlined dense hide-details="auto" no-filter no-data-text="Digite o nome da cidade" class="mb-3"></v-autocomplete>
                    <v-autocomplete v-model="office.airport_id" :items="airportOptions" label="Aeroporto de saída (opcional)" outlined dense hide-details="auto" clearable class="mb-3"></v-autocomplete>
                    <v-checkbox v-model="office.is_default" label="Sede padrão" dense hide-details class="mt-0"></v-checkbox>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="office.saving" @click="office.open = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('save')" color="primary" depressed :loading="office.saving" :disabled="!office.name || !office.municipality_id" @click="saveOffice">Salvar</v-btn>
                </div>
            </v-card>
        </v-dialog>

        <v-dialog v-model="airport.open" max-width="480" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Novo aeroporto</h3>
                        <p class="ui-section-description">Para aeroportos com voo regular que não estão na lista.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <div class="d-flex" style="gap: 12px;">
                        <v-text-field v-model="airport.iata" label="IATA" maxlength="3" outlined dense hide-details="auto" style="max-width: 100px;"></v-text-field>
                        <v-text-field v-model="airport.name" label="Nome" outlined dense hide-details="auto"></v-text-field>
                    </div>
                    <v-autocomplete v-model="airport.municipality_id" :items="airport.cityOptions" item-value="id" :item-text="cityText" :search-input.sync="airport.search"
                        label="Município" outlined dense hide-details="auto" no-filter no-data-text="Digite o nome da cidade" class="mt-3"></v-autocomplete>
                    <v-select v-model="airport.level" :items="levelItems" label="Porte" outlined dense hide-details="auto" :menu-props="{ offsetY: true }" class="mt-3"></v-select>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="airport.saving" @click="airport.open = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('save')" color="primary" depressed :loading="airport.saving" :disabled="!airport.iata || !airport.municipality_id" @click="saveAirport">Salvar</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import { notifyError, notifySuccess } from "@/components/prospect/prospect.util";

export default {
    name: "ProspectOffices",

    data: () => ({
        offices: [],
        airports: [],
        threshold: 5,
        savingThreshold: false,
        loading: false,
        airportScope: "ativos",
        airportSearch: "",
        office: { open: false, saving: false, id: null, name: "", municipality_id: null, airport_id: null, is_default: false, cityOptions: [], search: "" },
        airport: { open: false, saving: false, iata: "", name: "", municipality_id: null, level: "regional", cityOptions: [], search: "" },
        searchTimer: null,
        levelItems: [{ value: "major", text: "Grande (hub nacional)" }, { value: "hub", text: "Capital" }, { value: "regional", text: "Regional" }],
        airportHeaders: [
            { text: "Código", value: "iata", width: 90 },
            { text: "Aeroporto", value: "name" },
            { text: "Porte", value: "level", width: 220 },
            { text: "Em uso", value: "active", width: 100 },
        ],
    }),

    computed: {
        airportScopeItems() {
            return [
                { value: "ativos", text: "Em uso", count: this.airports.filter((item) => item.active).length },
                { value: "inativos", text: "Fora de uso", count: this.airports.filter((item) => !item.active).length },
                { value: "todos", text: "Todos", count: this.airports.length },
            ];
        },

        filteredAirports() {
            const term = String(this.airportSearch || "").trim().toLowerCase();
            return this.airports.filter((item) => {
                if (this.airportScope === "ativos" && !item.active) return false;
                if (this.airportScope === "inativos" && item.active) return false;
                return !term || `${item.iata} ${item.name} ${item.city} ${item.uf}`.toLowerCase().includes(term);
            });
        },

        airportOptions() {
            return this.airports.filter((item) => item.active).map((item) => ({ value: item.id, text: `${item.iata} · ${item.city}/${item.uf}` }));
        },
    },

    watch: {
        "office.search"(term) {
            this.searchCities(term, (list) => {
                const current = this.office.cityOptions.filter((item) => item.id === this.office.municipality_id);
                this.office.cityOptions = [...current, ...list.filter((item) => item.id !== this.office.municipality_id)];
            });
        },

        "airport.search"(term) {
            this.searchCities(term, (list) => {
                const current = this.airport.cityOptions.filter((item) => item.id === this.airport.municipality_id);
                this.airport.cityOptions = [...current, ...list.filter((item) => item.id !== this.airport.municipality_id)];
            });
        },
    },

    created() {
        this.load();
    },

    methods: {
        cityText(city) {
            return city ? `${city.name}/${city.uf}` : "";
        },

        searchCities(term, done) {
            clearTimeout(this.searchTimer);
            if (!term || term.length < 2) return;
            this.searchTimer = setTimeout(async () => {
                try {
                    const result = await axios.get("/api/admin/prospect/municipalities", { params: { q: term } });
                    done(result.data.content);
                } catch (error) {
                    notifyError(this, error);
                }
            }, 300);
        },

        async load() {
            this.loading = true;
            try {
                const [offices, airports] = await Promise.all([axios.get("/api/admin/prospect/offices"), axios.get("/api/admin/prospect/airports")]);
                this.offices = offices.data.content.offices;
                this.threshold = offices.data.content.flight_threshold_hours;
                this.airports = airports.data.content;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },

        openOffice(item = null) {
            this.office = {
                open: true,
                saving: false,
                id: item ? item.id : null,
                name: item ? item.name : "",
                municipality_id: item && item.municipality ? item.municipality.id : null,
                airport_id: item && item.airport ? item.airport.id : null,
                is_default: item ? item.is_default : false,
                cityOptions: item && item.municipality ? [item.municipality] : [],
                search: "",
            };
        },

        async saveOffice() {
            this.office.saving = true;
            try {
                const payload = { name: this.office.name, municipality_id: this.office.municipality_id, airport_id: this.office.airport_id || null, is_default: this.office.is_default };
                if (this.office.id) await axios.put(`/api/admin/prospect/offices/${this.office.id}`, payload);
                else await axios.post("/api/admin/prospect/offices", payload);
                this.office.open = false;
                notifySuccess(this, "Sede salva.");
                this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.office.saving = false;
            }
        },

        async makeDefault(item) {
            try {
                await axios.put(`/api/admin/prospect/offices/${item.id}`, { is_default: true });
                this.load();
            } catch (error) {
                notifyError(this, error);
            }
        },

        async removeOffice(item) {
            const confirmed = await this.$dialog.confirm({
                title: "Excluir sede",
                text: `"${item.name}" deixa de aparecer nas sugestões e nos roteiros novos. Roteiros que já saem dela continuam como estão.`,
                actions: { false: "Cancelar", true: { text: "Excluir", color: "error" } },
            });
            if (!confirmed) return;
            try {
                await axios.delete(`/api/admin/prospect/offices/${item.id}`);
                this.load();
            } catch (error) {
                notifyError(this, error);
            }
        },

        async saveThreshold() {
            this.savingThreshold = true;
            try {
                await axios.put("/api/admin/prospect/travel-settings", { flight_threshold_hours: this.threshold });
                notifySuccess(this, "Limite salvo.");
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.savingThreshold = false;
            }
        },

        async updateAirport(item, values) {
            try {
                const result = await axios.patch(`/api/admin/prospect/airports/${item.id}`, values);
                Object.assign(item, result.data.content);
            } catch (error) {
                notifyError(this, error);
                this.load();
            }
        },

        openAirport() {
            this.airport = { open: true, saving: false, iata: "", name: "", municipality_id: null, level: "regional", cityOptions: [], search: "" };
        },

        async saveAirport() {
            this.airport.saving = true;
            try {
                await axios.post("/api/admin/prospect/airports", { iata: this.airport.iata, name: this.airport.name, municipality_id: this.airport.municipality_id, level: this.airport.level });
                this.airport.open = false;
                notifySuccess(this, "Aeroporto incluído.");
                this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.airport.saving = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import "../../assets/scss/ui/tokens";

.po-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
        padding: 6px 8px;
        text-align: left;
        font-weight: 500;
        color: $ui-muted;
        border-bottom: 1px solid $ui-border;
    }

    td {
        padding: 8px;
        border-bottom: 1px solid $ui-border-soft;
    }

    .r {
        text-align: right;
    }
}

.po-threshold {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
}
</style>
